<template>
  <dx-modal
    :value="show"
    @input="close"
    title="Histórico"
    size="md"
    id="ophthalmology-anamnesis-history-modal"
  >
    <div class="btn-group mb-2" v-if="groups">
      <button
        v-for="(group, key) in groups"
        class="btn"
        :class="currentSelector === key ? 'btn-info' : 'btn-neutral'"
        @click="currentSelector = key"
        :key="key"
      >{{ group }}</button>
    </div>

    <table
      class="table table-striped table-hover table-history"
      v-if="tableData.items.length > 0"
    >
      <thead>
        <tr>
          <th style="width: 90px;">Data</th>
          <th v-if="isGroup" class="text-center">
<!--            <fa-icon :icon="['fal', 'eye']" />-->
          </th>
          <th
            v-for="(column, hIdx) in tableData.columns"
            :key="hIdx"
            :class="column.align ? `text-${column.align}` : ''"
          >{{ column.label }}</th>
          <th class="text-right" style="width: 2rem" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, i) in tableData.items" :key="i">
          <td>{{ item.date | date }}</td>
          <td v-if="isGroup" class="text-center">
            <span class="text-gray text-bold">D</span>
            <br/>
            <span class="text-gray text-bold">E</span>
          </td>
          <td
            v-for="n in item.cellCount || 0"
            :colspan="item.cellSpan || 1"
            :key="n"
            :class="tableData.columns[n-1].align ? `text-${tableData.columns[n-1].align}` : ''"
          >
            <div v-for="(row, rIdx) in item.rows" :key="rIdx">
              <span
                v-if="row.inputs[n-1].label"
                class="text-gray text-bold mr-1"
              >{{ row.inputs[n-1].label }}:</span>
              <span class="text-pre">{{ row.inputs[n-1].formattedValue || ' ' }}</span>
            </div>
          </td>
          <td>
            <button
              type="button"
              class="btn btn-sm btn-icon btn-action btn-gray-outline tooltip"
              data-tooltip="Inserir"
              @click="select(item)"
              :disabled="item.hasLegacy"
            ><fa-icon :icon="['fal', 'copy']" /></button>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="empty" v-else>
      <div class="empty-icon">
        <fa-icon :icon="['fal', 'info-circle']" size="2x"></fa-icon>
      </div>
      <p class="empty-title h5">Informação indisponível</p>
      <p class="empty-subtitle">
        <span>Nenhuma informação foi registrada até o momento</span>
        <span v-if="label"> para o campo "{{label}}"</span>
        <span>.</span>
      </p>
    </div>

    <template #footer>
      <button
        type="button"
        class="btn"
        @click="close"
      >Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { toText } from '@/helpers/html';
import LABELS from './labels';
import * as inputGroups from './inputGroups';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    history: {
      type: Array,
      default: () => [],
    },
    selector: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    groups: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      currentSelector: this.selector,
    };
  },
  computed: {
    isGroup() {
      return this.currentSelector in inputGroups;
    },
    inputs() {
      if (this.isGroup) {
        return [];
      }

      const match = this.currentSelector.match(/\(([^)]+)\)/);

      if (match) {
        const variants = match[1].split('|');
        const base = this.currentSelector.replace(/\([^)]+\)/, '{{var}}');
        return variants.map(variant => ({
          name: base.replace('{{var}}', variant),
          label: LABELS[variant] || variant,
        }));
      }

      return [{ name: this.currentSelector, label: null }];
    },
    availableHistory() {
      const names = this.inputs.map(({ name }) => name);
      return this.history
        .filter(({ anamnesis }) => (
          anamnesis.inputs
          && names.some(name => anamnesis.inputs[name])
        ));
    },
    tableData() {
      const data = {
        columns: [{ label: this.label || 'Valor' }],
        items: [],
      };

      if (this.isGroup) {
        data.columns = inputGroups[this.currentSelector]({}).columns;
        data.items = this.history
          .filter(({ anamnesis }) => anamnesis.inputs)
          .map(({ startDate, anamnesis }) => ({
            date: startDate,
            ...inputGroups[this.currentSelector](anamnesis.inputs),
          }));
      } else {
        data.items = this.availableHistory
          .map(({ startDate, anamnesis }) => ({
            date: startDate,
            rows: this.inputs.map((item) => {
              const value = anamnesis.inputs[item.name] || '';
              return {
                inputs: [{
                  name: item.name,
                  label: item.label,
                  value,
                  formattedValue: toText(anamnesis.inputs[item.name]),
                }],
              };
            }),
          }));
      }

      data.items = data.items
        .filter(({ rows }) => rows.length > 0)
        .map(item => ({
          ...item,
          cellCount: item.rows[0].inputs.length,
          cellSpan: data.columns.length - item.rows[0].inputs.length + 1,
          hasLegacy: item.rows.some(({ inputs }) => (
            inputs.some(({ name }) => name.includes('Legacy'))
          )),
        }));

      return data;
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    select(item) {
      const inputs = item.rows.reduce((acc, row) => {
        row.inputs.forEach(({ name, value }) => {
          acc[name] = value;
        });
        return acc;
      }, {});

      this.$emit('select', { inputs });
      this.close();
    },
  },
};
</script>

<style lang="scss">
#ophthalmology-anamnesis-history-modal {
  table {
    strong {
      font-weight: 600;
    }
  }
}
</style>
